import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import {
  WHLoginDataService,
  WHSafeTranslateService,
  WHToastService,
} from '@workheld/workheld-shared-lib';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NGXLogger } from 'ngx-logger';

const TOKEN_CONFIGURATION_ERROR_CODES = new Set([
  'invalid_grant',
  'missing_refresh_token',
]);
const FORCE_RELOAD_MESSAGE = /Login required|External interaction required/i;
const CHUNK_FAILED_MESSAGE = /Loading chunk \d+ failed/;
const CANCELED_REQUEST_MESSAGE = /abort|cancel/i;
const NULL_APPEND_CHILD_MESSAGE =
  /Cannot read properties of null \(reading 'appendChild'\)/;

interface UnhandledEventContext {
  [key: string]: string;
  type: string;
  target: string;
  targetTag: string;
  targetSource: string;
}

@Injectable()
export class ErrorHandlerService extends ErrorHandler {
  private static instance: ErrorHandlerService;

  constructor(
    private readonly safeTranslateService: WHSafeTranslateService,
    private readonly injector: Injector,
    private readonly loginDataService: WHLoginDataService,
    private readonly snackBar: MatSnackBar,
    private readonly ngxLogger: NGXLogger,
  ) {
    super();
    ErrorHandlerService.instance = this;
  }

  static handleGlobalError(error: any): void {
    if (ErrorHandlerService.instance) {
      ErrorHandlerService.instance.handleError(error);
    } else {
      console.error('ErrorHandlerService not initialized', error);
    }
  }

  // matches Angular's ErrorHandler.handleError(error: any) — this receives
  // raw Events, HttpErrorResponses, strings, and numbers, not just Error
  handleError(error: any): void {
    super.handleError(error);

    if (FORCE_RELOAD_MESSAGE.test(error?.message)) {
      window.location.reload();
      return;
    }

    if (
      CHUNK_FAILED_MESSAGE.test(error?.message) ||
      this._isStaleResourceLoadEvent(error)
    ) {
      this._promptPageRefresh();
      return;
    }

    this._reportError(error);
    this._showHttpErrorToast(error);
  }

  // native <script>/<link> load failures (e.g. a stale chunk URL after a
  // deployment) surface as a bare Event, not the webpack chunk-failed Error,
  // so they need their own reload prompt instead of noisy Sentry captures
  private _isStaleResourceLoadEvent(error: any): boolean {
    return (
      error instanceof Event &&
      error.type === 'error' &&
      (error.target instanceof HTMLScriptElement ||
        error.target instanceof HTMLLinkElement)
    );
  }

  private _promptPageRefresh(): void {
    this.snackBar
      // do not use translation key since it could happen before app init
      .open('Something went wrong. Please refresh the page.', 'Refresh')
      .onAction()
      .subscribe(() => window.location.reload());
  }

  // --- Sentry reporting ---

  private _reportError(error: any): void {
    if (window.location.hostname === 'localhost') {
      return;
    }

    if (this.loginDataService.activeUserDOM$) {
      Sentry.setContext('Workheld', this._buildWorkheldContext());
    }
    this._handleSentryError(error);
  }

  private _buildWorkheldContext(): Record<string, unknown> {
    return {
      'User ID': this.loginDataService.activeUserDOM$?.value?.id,
      User: this.loginDataService.activeUserDOM$?.value?.formattedName,
      Tenant: this.loginDataService.activeTenantDOM$?.value?.token,
    };
  }

  private _handleSentryError(error: any): void {
    // getAccessTokenSilently() failures arrive either directly or wrapped in
    // an unhandled promise rejection — both are expired sessions, not bugs
    const tokenError = error?.rejection ?? error;
    if (TOKEN_CONFIGURATION_ERROR_CODES.has(tokenError?.error)) {
      this.ngxLogger.error('Auth0 token request failed', {
        errorCode: tokenError.error,
        errorDescription: tokenError.error_description,
        audience: tokenError.audience,
        scope: tokenError.scope,
        ...this._buildWorkheldContext(),
      });
      return;
    }

    const { error: normalizedError, unhandledEventContext } =
      this._normalizeError(error);

    if (this._isIgnorableSentryError(normalizedError)) {
      return;
    }

    const originalError =
      normalizedError?.rejection ??
      normalizedError?.originalError ??
      normalizedError;

    if (!unhandledEventContext) {
      this._captureError(originalError);
      return;
    }

    Sentry.withScope((scope) => {
      const { type, targetTag, targetSource } = unhandledEventContext;
      scope.setContext('UnhandledEvent', unhandledEventContext);
      scope.setTag('unhandled_event.type', type);
      scope.setTag('unhandled_event.target', targetTag);
      // every synthesised event error is thrown from the same call site, so
      // Sentry's default grouping collapses unrelated failures into one
      // un-triageable issue — group by what actually failed instead
      scope.setFingerprint(['unhandled-event', type, targetTag, targetSource]);
      this._captureError(originalError);
    });
  }

  private _isIgnorableSentryError(error: any): boolean {
    if (error instanceof HttpErrorResponse) {
      return error.status === 0 || this._isCanceledRequest(error);
    }

    // Angular CDK's AriaDescriber can run its afterNextRender write against a
    // document whose <body> is already torn down (tooltip host destroyed
    // mid-navigation) — a benign CDK-internal race, not an application bug
    return (
      error instanceof TypeError &&
      NULL_APPEND_CHILD_MESSAGE.test(error.message) &&
      /AriaDescriber/.test(error.stack ?? '')
    );
  }

  private _captureError(error: any): void {
    Sentry.captureException(
      error instanceof Error
        ? error
        : new Error(String(error?.message || error)),
    );
  }

  private _normalizeError(error: any): {
    error: any;
    unhandledEventContext?: UnhandledEventContext;
  } {
    error =
      error?.rejection ??
      (error instanceof HttpErrorResponse
        ? error
        : (error?.error ?? error?.reason ?? error));

    // ErrorEvent (window.onerror script errors) is also an Event, so it must
    // be checked before the generic Event branch, or the message/filename/
    // line/col it carries would be lost.
    if (error?.isTrusted && error?.message && error?.filename) {
      const location = `${this._stripUrlParams(error.filename)}:${error.lineno}:${error.colno}`;

      return { error: new Error(`${error.message} (${location})`) };
    }

    if (error instanceof Event) {
      return this._buildUnhandledEventError(error);
    }

    return { error };
  }

  private _buildUnhandledEventError(event: Event): {
    error: Error;
    unhandledEventContext: UnhandledEventContext;
  } {
    const target: any = event.target;
    const tag = target?.tagName ?? target?.constructor?.name ?? 'unknown';
    const source = this._stripUrlParams(target?.src || target?.href || '');
    let description = source ? `${tag}(${source})` : tag;
    if (!target) {
      description = 'unknown target';
    }

    return {
      error: new Error(`Unhandled ${event.type} event on ${description}`),
      unhandledEventContext: {
        type: event.type,
        target: description,
        targetTag: tag,
        targetSource: source,
      },
    };
  }

  // asset URLs carry cache-busting hashes (which would fragment grouping) and
  // third-party script URLs carry API keys (which must not reach Sentry)
  private _stripUrlParams(url: string): string {
    return url.split('#')[0].split('?')[0];
  }

  private _isCanceledRequest(error: HttpErrorResponse): boolean {
    return CANCELED_REQUEST_MESSAGE.test(error.message ?? '');
  }

  // --- User-facing HTTP error toast ---

  private _showHttpErrorToast(error: any): void {
    if (!(error instanceof HttpErrorResponse)) {
      return;
    }

    const toastService = this.injector.get(WHToastService);

    // status 0 = request never reached the server (offline, gateway
    // unreachable, or the browser aborted it on route change) — not an
    // application error, so it must not look like one to the user.
    if (error.status === 0) {
      if (!this._isCanceledRequest(error)) {
        // do not use translation key: no backend error code exists for a
        // network-level failure, and the message must work before app init
        toastService.warning(
          'Could not load. Please try again. / Konnte nicht geladen werden. Bitte versuchen Sie es erneut.',
        );
      }
      return;
    }

    const err: any = error.error || error;
    toastService.error(this._buildHttpErrorMessage(err, error), {
      title: this._buildHttpErrorTitle(err),
    });
  }

  private _buildHttpErrorMessage(err: any, error: HttpErrorResponse): string {
    if (!err?.code) {
      return err.detail || err.message || error.message;
    }

    const key = 'ERROR.' + err.code;
    const translated = this.safeTranslateService.instant(key);
    // instant() echoes the key back when no translation exists
    const message = translated !== key ? translated : err.message;
    const affectedField = err.affectedFields?.[0];
    const fieldName =
      typeof affectedField === 'string'
        ? affectedField.split('::').pop()
        : undefined;

    return fieldName ? message.replace(/{{\w+}}/, fieldName) : message;
  }

  private _buildHttpErrorTitle(err: any): string | undefined {
    const status = err.status ? `(${err.status})` : '';

    return `${err.title || err.error || ''} ${status}`.trim() || undefined;
  }
}
